import React, { useEffect, useMemo, useState } from 'react';
import { Merchandiser } from '../../../redux/composableDevice/model/Merchandiser.model';
import { Box, Typography } from '@mui/material';
import ChannelConnection from '../../MerchandiserDetailPage/Channel/ChannelConnection';
import { useMetrics } from '../../../../../redux/monitoring/hooks/useMetrics';
import { getPolyglot } from '../../../../../i18n';
import WidthRefResize from '../../../../../util/renderProps/WidthRefResize';
import { determineAxisYNumber } from '../../../../../components/DeviceManager/Device/Monitoring/components/ComboCharts/determineAxisYNumber';
import { computeNumberOfUnits } from '../../../../../components/DeviceManager/Device/Monitoring/components/ComboCharts/computeNumberOfUnits';
import {
  LabelsXAxisTime,
  TIME_FRAME_TYPES,
  TIME_RANGE_TYPES,
} from 'stoerk-ui-components';
import LoadingButton from '@mui/lab/LoadingButton';
import { AnalogChannels } from '../../../../../components/DeviceManager/Device/Monitoring/components/ComboCharts/AnalogChannels/AnalogChannels';
import { useAppSelector } from '../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import moment from 'moment';
import _ from 'lodash';

const DeviceControlUnitOverview = (props: { merchandiser: Merchandiser }) => {
  const { merchandiser } = props;
  const channels = merchandiser?.channels;

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.machineType.id,
    })
  );

  const polyglot = getPolyglot();

  const initialTimeFrame = useMemo(
    () => ({
      start: moment().subtract(24, 'hours'),
      end: moment(),
      timeFrameType: TIME_FRAME_TYPES.LAST_24_HOURS,
      timeRangeType: TIME_RANGE_TYPES.QUICK,
    }),
    []
  );

  const merchandiserStandardViewChannels = JSON.parse(
    merchandiser?.composableDevice?.labels?.['standard_view_channels'] || '[]'
  );

  const composableDeviceStandardViewChannels = JSON.parse(
    machineType?.labels['standard_view_channels'] || '[]'
  );
  /* Get the selected sensor channel defined by the machine type*/
  const standardViewChannels =
    merchandiserStandardViewChannels.length > 0
      ? merchandiserStandardViewChannels
      : composableDeviceStandardViewChannels || [];
  if (standardViewChannels.length === 0) {
    return null;
  }

  const [selectedIndex, setSelectedIndex] = useState(
    standardViewChannels.length > 0 ? standardViewChannels[0] : 0
  );
  const selectedChannel = useMemo(
    () => channels[selectedIndex],
    [channels, selectedIndex]
  );

  const defaultAnalogFilter = useMemo(() => {
    return {
      [merchandiser.deviceId]: {
        [selectedChannel.controlUnitId]: { [selectedChannel.channel]: true },
      },
    };
  }, [selectedChannel, merchandiser.id]);

  const {
    timeControls: { timeFrame },
    metrics,
    loading,
    analogControls: [_, setAnalogFilter],
  } = useMetrics({
    showAllMetrics: true,
    initialTimeFrame,
    defaultAnalogFilter,
  });

  useEffect(() => {
    setAnalogFilter(defaultAnalogFilter);
  }, [defaultAnalogFilter]);

  const [internalTimeFrame, setInternalTimeFrame] = useState(timeFrame);
  useEffect(() => {
    setInternalTimeFrame(timeFrame);
  }, [timeFrame]);

  const numberOfUnits = useMemo(
    () => metrics.sensordata && computeNumberOfUnits(metrics.sensordata),
    [metrics.sensordata]
  );
  const maxVAxisToShow = useMemo(
    () => determineAxisYNumber(numberOfUnits || 0),
    [numberOfUnits]
  );
  const widthVAxis = 20 * maxVAxisToShow;

  const labels = useMemo(
    () => ({
      type: 'HTML',
      maxVAxis: maxVAxisToShow,
      widthVAxis: widthVAxis,
      showHAxis: false,
    }),
    [maxVAxisToShow, widthVAxis]
  );

  const sensorData = useMemo(() => metrics.sensordata, [metrics.sensordata]);
  return (
    <Box
      display={'grid'}
      gridTemplateColumns={{
        xs: '1fr',
        sm: '1.2fr 4fr',
      }}
      gap={'0.8rem'}
      gridAutoRows={'1fr'}
    >
      <Box
        sx={{
          height: 'fit-content',
          gap: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {standardViewChannels.map((channelIndex: number) => (
          <ChannelConnection
            channel={channels[channelIndex]}
            merchandiser={merchandiser}
            key={channels[channelIndex]?.name}
            onClick={() => setSelectedIndex(channelIndex)}
            selected={selectedIndex === channelIndex}
          ></ChannelConnection>
        ))}
      </Box>
      <Box
        sx={{
          background: (theme) => theme.palette.common.white,
          padding: '1rem',
        }}
      >
        {loading ? (
          <LoadingButton
            sx={{ width: '100%', alignSelf: 'center' }}
            loading={true}
          />
        ) : (
          <WidthRefResize
            render={(width) => (
              <>
                <Typography variant="h4" color="primary.main">
                  {selectedChannel.name}
                </Typography>
                <AnalogChannels
                  data={sensorData}
                  polyglot={polyglot}
                  setInternalTimeFrame={setInternalTimeFrame}
                  externalTimeFrame={timeFrame}
                  labels={labels}
                  width={width - widthVAxis}
                  height={600}
                  controls={[]}
                  zoomEnable={false}
                  key={merchandiser.id}
                  hideLegend={true}
                />
                {sensorData.length > 0 ? (
                  <LabelsXAxisTime
                    widthVAxis={widthVAxis}
                    width={width - widthVAxis}
                    minDatetime={internalTimeFrame.start.unix()}
                    maxDatetime={internalTimeFrame.end.unix()}
                  />
                ) : null}
              </>
            )}
          />
        )}
      </Box>
    </Box>
  );
};

const MemoizedDeviceControlUnitOverview = React.memo(DeviceControlUnitOverview);
export { MemoizedDeviceControlUnitOverview as DeviceControlUnitOverview };
